import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { warehouseApi } from '../../api/warehouse.api';
import { goodsReceiptHistory, HistorySection } from '../../components/history';
import { PoTypeTag } from '../../components/orderType';
import {
  codeCls,
  dateCls,
  DetailHeader,
  DetailSkeleton,
  KV,
  LoadError,
  linkCls,
  MappedBadge,
  metaCls,
  nameCls,
  numCls,
  Section,
} from '../../components/ui';
import { fmtDate, fmtDateTime, fmtQty } from '../../format';
import { useLoad } from '../../hooks/useLoad';
import WarehousePrintModal, {
  type PrintTarget,
} from '../../print/WarehousePrintModal';
import { GOODS_RECEIPT_STATUS, poSourceOf, RECEIPT_STATUS } from '../../status';
import type { GoodsReceiptLine } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

const COLUMNS: TableDataColumn<GoodsReceiptLine>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => (
      <div>
        <p className={codeCls}>{l.sku}</p>
        <p className={metaCls}>{l.itemName}</p>
      </div>
    ),
  },
  {
    key: 'qty',
    title: 'Received qty',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qty)}</span>,
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
  {
    key: 'expiredDate',
    title: 'Exp',
    render: (_v, l) => (
      <span className={dateCls}>
        {l.expiredDate ? fmtDate(l.expiredDate) : '—'}
      </span>
    ),
  },
  {
    key: 'bin',
    title: 'Storage destination',
    render: (_v, l) => (
      <span className={nameCls}>{l.storagePath || l.storageName}</span>
    ),
  },
  {
    key: 'batchNumber',
    title: 'Batch number',
    render: (_v, l) => <span className={numCls}>{l.batchNumber ?? '—'}</span>,
  },
];

const GoodsReceiptDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { refreshToken } = useWarehouse();
  const detail = useLoad(
    (signal) => warehouseApi.getGoodsReceipt(id, { signal }),
    [id, refreshToken]
  );
  const poID = detail.data?.receipt.purchaseOrderID ?? null;
  const po = useLoad(
    (signal) => warehouseApi.getPurchaseOrder(poID as string, { signal }),
    [poID],
    !!poID
  );
  const [print, setPrint] = useState<PrintTarget | null>(null);

  if (detail.loading && !detail.data) return <DetailSkeleton />;
  if (detail.error || !detail.data) {
    return (
      <div className="space-y-5 p-6">
        <DetailHeader
          backTo={WAREHOUSE_PATHS.goodsReceipt}
          backLabel="Goods receipt"
          title={id}
        />
        <LoadError
          message={detail.error?.message ?? 'Goods receipt tidak ditemukan'}
          onRetry={detail.reload}
        />
      </div>
    );
  }
  const {
    receipt: r,
    lines,
    deliveryNotePhotoUrl,
    closesBackorder,
    notes,
  } = detail.data;
  const posted = r.status === 'POSTED';
  const order = po.data?.order ?? null;
  // GR rows carry only the source name; the PO tells its type.
  const source = order
    ? poSourceOf(order)
    : { label: 'Supplier', value: r.supplierName, sub: null };

  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.goodsReceipt}
        backLabel="Goods receipt"
        meta={
          <>
            Dibuat {fmtDateTime(r.createdAt)} · oleh{' '}
            <span className="font-semibold text-text-sub-600">
              {r.createdBy}
            </span>
          </>
        }
        title={r.code}
        badges={<MappedBadge map={GOODS_RECEIPT_STATUS} value={r.status} />}
        actions={
          <>
            {posted && (
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => setPrint({ kind: 'gr', id: r.id })}
                leftIcon={<i className="ri-printer-line text-[14px]" />}
              >
                Print goods receipt
              </Button>
            )}
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => navigate(WAREHOUSE_PATHS.stock)}
              leftIcon={<i className="ri-stack-line text-[14px]" />}
            >
              Lihat stock
            </Button>
          </>
        }
      />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          <Section title="Receipt information">
            <TableData size="sm" data={lines} columns={COLUMNS} />
          </Section>
          <HistorySection entries={goodsReceiptHistory(r, closesBackorder)} />
        </div>
        <div className="flex flex-col gap-5">
          <Section title="Shipping information">
            <KV
              label="PO number"
              value={
                r.purchaseOrderID ? (
                  <Link
                    to={`${WAREHOUSE_PATHS.purchaseOrder}/${r.purchaseOrderID}`}
                    className={linkCls}
                  >
                    {r.purchaseOrderCode ?? r.purchaseOrderID}
                  </Link>
                ) : null
              }
            />
            <KV
              label="Client"
              value={r.client?.name ?? `Client #${r.clientID}`}
            />
            {order && (
              <KV label="Tipe PO" value={<PoTypeTag type={order.type} />} />
            )}
            <KV
              label={source.label}
              value={source.value}
              sub={source.sub ?? undefined}
            />
            {order?.type === 'TRANSFER' && order.hubTransfer && (
              <KV
                label="Transfer warehouse"
                value={
                  <Link
                    to={`${WAREHOUSE_PATHS.hubTransfer}/${order.hubTransfer.id}`}
                    className={linkCls}
                  >
                    {order.hubTransfer.code}
                  </Link>
                }
              />
            )}
            {order?.type === 'TRANSFER' && order.transferOutboundOrder && (
              <KV
                label="Outbound transfer"
                value={
                  <Link
                    to={`${WAREHOUSE_PATHS.outboundRequest}/${order.transferOutboundOrder.id}`}
                    className={linkCls}
                  >
                    {order.transferOutboundOrder.code}
                  </Link>
                }
              />
            )}
            <KV label="Hub" value={r.hub?.name ?? `Hub #${r.hubID}`} />
            <KV label="Date arrival" value={fmtDateTime(r.arrivedAt)} />
            <KV
              label="Surat jalan supplier"
              value={
                r.deliveryNoteNo ? (
                  <span className={codeCls}>{r.deliveryNoteNo}</span>
                ) : null
              }
            />
            <KV
              label="Surat jalan (PDF)"
              value={
                deliveryNotePhotoUrl ? (
                  <a
                    href={deliveryNotePhotoUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-accent hover:underline"
                  >
                    Buka PDF
                  </a>
                ) : null
              }
            />
            <KV
              label="Tutup sisa PO"
              value={closesBackorder ? 'Ya' : 'Tidak'}
            />
            {notes && <KV label="Catatan" value={notes} />}
            {po.data?.order.receiptStatus && (
              <KV
                label="Receiving PO"
                value={
                  <MappedBadge
                    map={RECEIPT_STATUS}
                    value={po.data.order.receiptStatus}
                  />
                }
              />
            )}
          </Section>
        </div>
      </div>
      <WarehousePrintModal target={print} onClose={() => setPrint(null)} />
    </div>
  );
};

export default GoodsReceiptDetailPage;
